import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CloudProvider } from '@ariksa/abac/api';
import { CloudProviders } from '@ariksa/cloud-account';
import { NativeResources } from '@ariksa/inventory-core';
import { InsightV2ApiInsightV2AllRequest } from '@ariksa/inventory-core/api';
import { CloudAccountInfoGetResponse } from '@ariksa/jit/api';
import { Box, HStack, Stack } from '@chakra-ui/react';
import { customTheme } from 'theme';
import { isEmpty } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';
import { QueryError } from 'services/types';
import { INIT_PAGE_INFO } from 'services/utils/constants';

import { InventoryService } from 'api/services';
import { CancelButton, Form, PrimaryButton } from 'components/DataEntry';
import {
  AtIcon,
  ExclamationTriangleIcon,
  IconTypes,
  OktaIcon,
  UserIcon,
} from 'components/Icons';
import { getIcon } from 'components/Icons/Components';
import { Modal } from 'components/Overlay';
import { selectApp, selectUser } from 'containers/App/selectors';
import { selectJit } from 'containers/Jit/selectors';
import { jitFormStyles } from 'containers/Jit/Setup/Components/styles';
import { JITConfigurationModalHeader } from 'containers/Jit/Setup/Components/Utils/JITConfigurationModalHeader';
import { JITInformationAlert } from 'containers/Jit/Setup/Components/Utils/JITInformationAlert';
import {
  getJITDirectoryDescription,
  getJITIdentitySource,
  getJITLoadErrorMessage,
  getJITSaveErrorMessage,
  JIT_NOT_SAVED_MESSAGE,
} from 'containers/Jit/Setup/Components/Utils/utils';
import { actions as setupActions } from 'containers/Jit/Setup/slice';
import { actions as jitActions } from 'containers/Jit/slice';

interface JitApproverModalProps {
  // Shared by account and data-group JIT configuration.
  account: CloudAccountInfoGetResponse;
  isOpen: boolean;
  onClose: () => void;
  viewBy?: string;
  dataGroupId?: string;
  dataGroupName?: string;
}

const defaultDirectoryOption = {
  label: 'None',
  value: 'manual',
  type: 'manual',
  icon: <AtIcon />,
};

export const ConfigureJITApprovers = (props: JitApproverModalProps) => {
  const { account, viewBy, dataGroupId, dataGroupName } = props;
  // Read optionally: the slice is injected by an ancestor route, and a reducer
  // swapped in over an existing store keeps the state object it already had, so
  // a newly added key can be missing until the store is rebuilt.
  const { approvers: jitApprovers, jitAction, jitUsers } = useSelector(
    selectJit,
  );
  const dispatch = useDispatch();

  const { isOpen, onClose } = props;
  const { info } = useSelector(selectUser);
  const { cloudAccounts } = useSelector(selectApp);

  const [approvers, setApprovers] = useState<Record<string, any>[]>([]);
  const [userOptions, setUserOptions] = useState<Record<string, any>[]>([]);
  const [emailIds, setEmailIds] = useState<string[]>([]);
  const [directoryType, setDirectoryType] = useState<Record<string, any>>(
    defaultDirectoryOption,
  );
  const [isOktaUsersLoading, setIsOktaUsersLoading] = useState(false);
  const [loadError, setLoadError] = useState<QueryError>();
  const [saveError, setSaveError] = useState<string>();

  const oktaDirectoryOptions = useMemo(
    () =>
      cloudAccounts.data
        .filter(account => account.cloud_type === CloudProviders.Okta)
        .map(oktaAccount => ({
          label: `${oktaAccount.name} (${oktaAccount.cloud_account_id})`,
          value: oktaAccount.uuid,
          type: 'okta',
          icon: <OktaIcon />,
        })),
    [cloudAccounts.data],
  );

  const directoryOptions = useMemo(
    () => [
      defaultDirectoryOption,
      {
        label: 'Ariksa',
        value: 'ariksa',
        type: 'ariksa',
        icon: getIcon(IconTypes.AriksaLogoOnly),
      },
      ...oktaDirectoryOptions,
    ],
    [oktaDirectoryOptions],
  );

  useEffect(() => {
    setDirectoryType(
      viewBy === 'data'
        ? directoryOptions[0] ?? defaultDirectoryOption
        : defaultDirectoryOption,
    );
  }, [account.uuid, directoryOptions, viewBy]);

  useEffect(() => {
    if (!isOpen || !account.uuid) return;

    setLoadError(undefined);
    dispatch(
      jitActions.getApprovers({
        q: {
          accountId: account.uuid,
          dataGroupId,
        },
        onError: error => setLoadError(error),
      }),
    );
  }, [account.uuid, dataGroupId, dispatch, isOpen]);

  // Reading one data group also returns the account-wide approvers, because
  // they can approve for it. Only the group's own are editable here: sending an
  // account-wide one back would write a second row for the same person, who
  // would then be listed twice everywhere.
  const inheritedApprovers = useMemo(
    () =>
      dataGroupId
        ? (jitApprovers.data ?? [])
            .filter(approver => !approver.data_group_id)
            .map(approver => approver.email)
        : [],
    [dataGroupId, jitApprovers.data],
  );

  // On a Snowflake account with an IdP every request belongs to a data group,
  // and that group's creator and owner approve it.
  const isDataGroupManaged =
    !dataGroupId &&
    account.cloud_type === CloudProvider.Snowflake &&
    !!account.is_idp_enabled;

  // JIT refuses an empty list that leaves requests nobody can approve: the
  // account's may be empty only where data groups cover every request, and a
  // group's only while account-wide approvers cover it.
  const canSaveEmpty = dataGroupId
    ? !!inheritedApprovers.length
    : isDataGroupManaged;

  // The saved approvers as both lists hold them. Used to fill the form and to
  // put it back when the directory changes, so switching never empties a list
  // that a save would then write back as the whole set.
  //
  // Scoped to exactly what this form saves. The read is wider than the write in
  // both directions: editing a group also returns the account-wide approvers,
  // and editing the account returns every group's. Sending either back would
  // move those people into the scope being saved.
  const savedApprovers = useMemo(
    () =>
      (jitApprovers.data ?? [])
        .filter(approver =>
          dataGroupId
            ? approver.data_group_id === dataGroupId
            : !approver.data_group_id,
        )
        .map(approver => ({
          label: approver.email,
          value: approver.email,
        })),
    [dataGroupId, jitApprovers.data],
  );

  const seedFromSaved = useCallback(() => {
    setApprovers(savedApprovers);
    setEmailIds(savedApprovers.map(approver => approver.value));
  }, [savedApprovers]);

  useEffect(() => {
    seedFromSaved();
  }, [seedFromSaved]);

  useEffect(() => {
    if (directoryType?.type === 'okta') {
      if (!directoryType.value) {
        setUserOptions([]);
        return;
      }

      const payload: InsightV2ApiInsightV2AllRequest = {
        insightV2RequestBase: {
          resource_type: NativeResources.OktaUser,
          account_id: [directoryType.value],
        },
      };

      setIsOktaUsersLoading(true);
      InventoryService.InsightV2.insightV2All(payload)
        .then(response => {
          setUserOptions(
            map(response.data?.items, user => {
              const value = user?.email ?? user?.name ?? user?.uuid;

              return {
                label: value,
                value,
                data: user,
              };
            }),
          );
        })
        .catch(() => setUserOptions([]))
        .finally(() => setIsOktaUsersLoading(false));

      return;
    }

    if (directoryType?.type !== 'ariksa') {
      setUserOptions([]);
      return;
    }

    dispatch(
      jitActions.getJitUsers({
        q: {
          organization: info.org_id,
        },
        onSuccess: r =>
          setUserOptions(
            map(r?.users, user => ({
              label: user.email,
              value: user.email,
            })),
          ),
      }),
    );
  }, [
    account.uuid,
    directoryType?.type,
    directoryType?.value,
    dispatch,
    info.org_id,
  ]);

  // Until the saved list has arrived, the form cannot show it, so editing or
  // saving would replace approvers nobody has seen. A failed load stays locked
  // for the same reason: empty here means "not loaded", not "none set".
  const isLoaded = jitApprovers.isSuccess;

  const handleSummit = () => {
    setSaveError(undefined);
    const identitySource = getJITIdentitySource(directoryType?.type);
    const emails =
      directoryType?.type === 'manual'
        ? emailIds
        : approvers.map(a => a?.value);

    dispatch(
      jitActions.editApprovers({
        q: {
          accountId: account.uuid ?? '',
          approverUpdate: {
            approvers: emails.map(email => ({
              email,
              identity_source: identitySource,
            })),
          },
          dataGroupId,
        },
        onSuccess: res => {
          // JIT reports a refused save as FAILED in a successful response.
          if (res === 'FAILED') {
            setSaveError(JIT_NOT_SAVED_MESSAGE);
            return;
          }
          onClose();
          if (viewBy !== 'data') {
            dispatch(
              setupActions.getJitAccounts({
                q: {},
                page: INIT_PAGE_INFO,
              }),
            );
          }
        },
        onError: error =>
          setSaveError(getJITSaveErrorMessage(error, dataGroupId)),
      }),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <JITConfigurationModalHeader
          label={viewBy === 'data' ? 'Set Approvers' : 'Configure Approvers'}
          account={account}
          dataGroupName={dataGroupName}
          icon={<UserIcon />}
        />
      }
      size="lg"
      styles={{ body: { pb: 0, px: 6 } }}
      body={
        <Stack w="full" h="full" spacing={5}>
          <JITInformationAlert
            icon={<ExclamationTriangleIcon color="red" />}
            description={
              loadError
                ? getJITLoadErrorMessage(loadError, dataGroupId)
                : saveError ?? getJITDirectoryDescription(directoryType?.type)
            }
          />
          {!loadError && !!inheritedApprovers.length && (
            <Box px={12} fontSize="13px" color={customTheme.colors.gray['250']}>
              Also approved by {inheritedApprovers.join(', ')}, who approve for
              the whole account. Change them under the account's approvers.
            </Box>
          )}
          {!loadError && isDataGroupManaged && (
            <Box px={12} fontSize="13px" color={customTheme.colors.gray['250']}>
              Each data group's creator and owner approve its requests. Add
              someone here only if they should approve for every data group.
            </Box>
          )}
          <Box px={12} hidden={!!loadError}>
            <Form
              schema={{
                directory: {
                  type: 'react-select',
                  label: 'Directory',
                  options: directoryOptions,
                  value: directoryType,
                  onChange: val => {
                    setDirectoryType(val);
                    seedFromSaved();
                  },
                  isDisabled: !isLoaded,
                  showIconInValueContainer: true,
                  valueContainerIcon: directoryType?.icon,
                },
                ...(directoryType?.type === 'manual'
                  ? {
                      approvers: {
                        type: 'emailIds',
                        label: 'Enter Users (Please press enter to add)',
                        value: emailIds,
                        onChange: setEmailIds,
                        placeholder: 'Enter approver email ID',
                        isDisabled: !isLoaded,
                      },
                    }
                  : {
                      approvers: {
                        type: 'react-select',
                        label: 'Select Users',
                        isMulti: true,
                        isLoading:
                          directoryType?.type === 'okta'
                            ? isOktaUsersLoading
                            : jitUsers?.isLoading,
                        value: approvers,
                        options: userOptions,
                        showTotalSelected: true,
                        showSelectedAtBottom: true,
                        onChange: setApprovers,
                        isDisabled:
                          !isLoaded ||
                          (directoryType?.type === 'okta'
                            ? isOktaUsersLoading
                            : jitUsers?.isLoading),
                        placeholder: 'Select approvers',
                      },
                    }),
              }}
              handleSubmit={handleSummit}
              buttonOptions={{
                submit: {
                  name: 'Update',
                  isVisible: false,
                },
              }}
              styles={jitFormStyles}
            />
          </Box>
        </Stack>
      }
      footer={
        <HStack justify="space-between" w="full">
          <CancelButton onClick={onClose}>CANCEL</CancelButton>
          <PrimaryButton
            px={5}
            onClick={handleSummit}
            isLoading={jitAction.isLoading}
            isDisabled={
              !isLoaded ||
              (!canSaveEmpty &&
                isEmpty(
                  directoryType?.type === 'manual' ? emailIds : approvers,
                ))
            }
          >
            OK
          </PrimaryButton>
        </HStack>
      }
    />
  );
};
